import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiMenu,
  FiUser,
  FiLogOut,
  FiShield,
  FiLock,
  FiLayers,
  FiCheckCircle,
  FiBell,
  FiAlertTriangle,
  FiClock,
  FiArrowRight,
  FiBox,
  FiRefreshCw,
  FiX,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { authApi, dashboardApi } from "../services/api";
import Modal from "./Modal";
import logoImg from "../assets/Icons/Logo.png";

const Navbar = ({ onToggleSidebar }) => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [passwords, setPasswords] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [submittingPwd, setSubmittingPwd] = useState(false);

  // Notification Alerts State (Stock Bottom Line & Warranty Expiry)
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [activeAlertTab, setActiveAlertTab] = useState("all"); // 'all' | 'stock' | 'warranty'
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsData, setAlertsData] = useState({
    bottomLineAlerts: { items: [], totalCount: 0 },
    warrantyAlerts: { items: [], totalCount: 0 },
    totalAlerts: 0,
  });

  const alertsDropdownRef = useRef(null);

  const fetchAlerts = useCallback(async (isManual = false) => {
    try {
      if (isManual) setAlertsLoading(true);
      const res = await dashboardApi.getAlerts();
      if (res && res.data) {
        setAlertsData({
          bottomLineAlerts: res.data.bottomLineAlerts || { items: [], totalCount: 0 },
          warrantyAlerts: res.data.warrantyAlerts || { items: [], totalCount: 0 },
          totalAlerts: res.data.totalAlerts || 0,
        });
      }
    } catch (err) {
      // Background poll failure silent unless manual
      if (isManual) {
        toast.error("Failed to load notifications");
      }
    } finally {
      if (isManual) setAlertsLoading(false);
    }
  }, []);

  // Fetch alerts on mount and every 45 seconds for real-time status
  useEffect(() => {
    fetchAlerts();
    const timer = setInterval(() => {
      fetchAlerts();
    }, 45000);
    return () => clearInterval(timer);
  }, [fetchAlerts]);

  // Handle outside click for alerts dropdown
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (alertsDropdownRef.current && !alertsDropdownRef.current.contains(e.target)) {
        setAlertsOpen(false);
      }
    };
    if (alertsOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [alertsOpen]);

  const handleLogout = () => {
    logout();
    toast.info("Logged out successfully");
    navigate("/login");
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    try {
      setSubmittingPwd(true);
      await authApi.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      toast.success("Password changed successfully!");
      setPwdModalOpen(false);
      setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      toast.error(err.message || "Failed to update password");
    } finally {
      setSubmittingPwd(false);
    }
  };

  const bottomLineItems = alertsData.bottomLineAlerts?.items || [];
  const bottomLineCount = alertsData.bottomLineAlerts?.totalCount || bottomLineItems.length;
  const warrantyItems = alertsData.warrantyAlerts?.items || [];
  const warrantyCount = alertsData.warrantyAlerts?.totalCount || warrantyItems.length;
  const totalAlertCount = alertsData.totalAlerts || (bottomLineCount + warrantyCount);

  return (
    <>
      <header
        className="navbar-header"
        style={{
          height: 64,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid var(--slate-200)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            onClick={onToggleSidebar}
            aria-label="Toggle Navigation"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 8,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--slate-600)",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-100)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <FiMenu size={22} />
          </button>

          <Link to="/dashboard" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <img
              src={logoImg}
              alt="Commercial IT"
              style={{
                height: 38,
                maxHeight: 38,
                width: "auto",
                objectFit: "contain",
                borderRadius: 6,
              }}
            />
            <div>
              <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--navy)", lineHeight: 1.1 }}>
                Commercial <span style={{ color: "var(--accent)" }}>IT</span>
              </div>
              <div className="navbar-brand-subtitle" style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontWeight: 500 }}>
                Inventory Management System
              </div>
            </div>
          </Link>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Notification Bell Icon & Dropdown */}
          <div ref={alertsDropdownRef} style={{ position: "relative" }}>
            <button
              onClick={() => {
                setAlertsOpen(!alertsOpen);
                if (!alertsOpen) {
                  fetchAlerts(true);
                }
              }}
              aria-label="Notifications"
              title={`${totalAlertCount} active alerts (${bottomLineCount} Bottom Line, ${warrantyCount} Warranty)`}
              style={{
                background: alertsOpen ? "var(--slate-100)" : "none",
                border: "1px solid var(--slate-200)",
                cursor: "pointer",
                padding: "8px 10px",
                borderRadius: "var(--radius-md)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: totalAlertCount > 0 ? "#D97706" : "var(--slate-600)",
                position: "relative",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-100)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = alertsOpen ? "var(--slate-100)" : "transparent")}
            >
              <FiBell size={20} />
              {totalAlertCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    backgroundColor: "#DC2626",
                    color: "#ffffff",
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    minWidth: 18,
                    height: 18,
                    borderRadius: 9,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                    boxShadow: "0 2px 6px rgba(220, 38, 38, 0.4)",
                    animation: "pulse 2s infinite",
                  }}
                >
                  {totalAlertCount > 99 ? "99+" : totalAlertCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown Panel */}
            {alertsOpen && (
              <div
                className="navbar-alerts-dropdown"
                style={{
                  position: "absolute",
                  right: 0,
                  marginTop: 10,
                  width: 380,
                  maxHeight: 520,
                  backgroundColor: "#ffffff",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "0 14px 40px rgba(0,0,0,0.16), 0 4px 12px rgba(0,0,0,0.06)",
                  border: "1px solid var(--slate-200)",
                  zIndex: 250,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  animation: "fadeIn 0.18s ease-out",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "14px 18px",
                    borderBottom: "1px solid var(--slate-200)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "var(--slate-50)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        backgroundColor: "#FEF3C7",
                        color: "#D97706",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FiBell size={15} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: "0.94rem", color: "var(--navy)" }}>
                        Notifications
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
                        Stock limit & warranty alerts
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      onClick={() => fetchAlerts(true)}
                      title="Refresh Alerts"
                      disabled={alertsLoading}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--slate-500)",
                        padding: 4,
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FiRefreshCw size={14} style={{ animation: alertsLoading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <button
                      onClick={() => setAlertsOpen(false)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--slate-400)",
                        padding: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div
                  style={{
                    display: "flex",
                    backgroundColor: "#fff",
                    borderBottom: "1px solid var(--slate-200)",
                    padding: "0 12px",
                  }}
                >
                  <button
                    onClick={() => setActiveAlertTab("all")}
                    style={{
                      flex: 1,
                      padding: "10px 6px",
                      background: "none",
                      border: "none",
                      borderBottom: activeAlertTab === "all" ? "2.5px solid var(--primary)" : "2.5px solid transparent",
                      color: activeAlertTab === "all" ? "var(--primary)" : "var(--slate-600)",
                      fontWeight: activeAlertTab === "all" ? 800 : 600,
                      fontSize: "0.78rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >
                    <span>All</span>
                    <span
                      style={{
                        fontSize: "0.70rem",
                        padding: "1px 6px",
                        borderRadius: 10,
                        backgroundColor: activeAlertTab === "all" ? "var(--primary-light)" : "var(--slate-100)",
                        color: activeAlertTab === "all" ? "var(--primary)" : "var(--slate-600)",
                      }}
                    >
                      {totalAlertCount}
                    </span>
                  </button>

                  <button
                    onClick={() => setActiveAlertTab("stock")}
                    style={{
                      flex: 1,
                      padding: "10px 6px",
                      background: "none",
                      border: "none",
                      borderBottom: activeAlertTab === "stock" ? "2.5px solid #D97706" : "2.5px solid transparent",
                      color: activeAlertTab === "stock" ? "#D97706" : "var(--slate-600)",
                      fontWeight: activeAlertTab === "stock" ? 800 : 600,
                      fontSize: "0.78rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >
                    <span>Bottom Line</span>
                    <span
                      style={{
                        fontSize: "0.70rem",
                        padding: "1px 6px",
                        borderRadius: 10,
                        backgroundColor: activeAlertTab === "stock" ? "#FEF3C7" : "var(--slate-100)",
                        color: activeAlertTab === "stock" ? "#B45309" : "var(--slate-600)",
                      }}
                    >
                      {bottomLineCount}
                    </span>
                  </button>

                  <button
                    onClick={() => setActiveAlertTab("warranty")}
                    style={{
                      flex: 1,
                      padding: "10px 6px",
                      background: "none",
                      border: "none",
                      borderBottom: activeAlertTab === "warranty" ? "2.5px solid #EA580C" : "2.5px solid transparent",
                      color: activeAlertTab === "warranty" ? "#EA580C" : "var(--slate-600)",
                      fontWeight: activeAlertTab === "warranty" ? 800 : 600,
                      fontSize: "0.78rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >
                    <span>Warranty</span>
                    <span
                      style={{
                        fontSize: "0.70rem",
                        padding: "1px 6px",
                        borderRadius: 10,
                        backgroundColor: activeAlertTab === "warranty" ? "#FFEDD5" : "var(--slate-100)",
                        color: activeAlertTab === "warranty" ? "#C2410C" : "var(--slate-600)",
                      }}
                    >
                      {warrantyCount}
                    </span>
                  </button>
                </div>

                {/* Notification Items List */}
                <div style={{ flex: 1, overflowY: "auto", maxHeight: 330, backgroundColor: "#fff" }}>
                  {totalAlertCount === 0 ? (
                    <div style={{ padding: "36px 20px", textAlign: "center", color: "var(--slate-400)" }}>
                      <FiCheckCircle size={32} style={{ color: "var(--primary)", marginBottom: 8 }} />
                      <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.92rem" }}>
                        All Good!
                      </div>
                      <div style={{ fontSize: "0.78rem", marginTop: 4 }}>
                        No stock bottom-line limits reached and no warranties expiring.
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* 1. STOCK BOTTOM LINE ALERTS */}
                      {(activeAlertTab === "all" || activeAlertTab === "stock") && (
                        <>
                          {bottomLineItems.map((item, idx) => {
                            const isOut = item.current_stock <= 0;
                            const relatedValueName = item.related_value_name || item.subcategory_name || item.item_name;
                            return (
                              <div
                                key={`stock-alert-${idx}`}
                                onClick={() => {
                                  setAlertsOpen(false);
                                  navigate(`/stock?search=${encodeURIComponent(relatedValueName)}`);
                                }}
                                style={{
                                  padding: "12px 16px",
                                  borderBottom: "1px solid var(--slate-100)",
                                  display: "flex",
                                  gap: 12,
                                  cursor: "pointer",
                                  transition: "background 0.15s ease",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                              >
                                <div
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    backgroundColor: isOut ? "#FEE2E2" : "#FEF3C7",
                                    color: isOut ? "#DC2626" : "#D97706",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    marginTop: 2,
                                  }}
                                >
                                  <FiAlertTriangle size={16} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                                    <span style={{ fontWeight: 800, fontSize: "0.88rem", color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {relatedValueName}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "0.68rem",
                                        fontWeight: 800,
                                        padding: "1px 6px",
                                        borderRadius: 4,
                                        backgroundColor: isOut ? "#FEE2E2" : "#FEF3C7",
                                        color: isOut ? "#DC2626" : "#B45309",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {isOut ? "Out of Stock" : "Bottom Line"}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: "0.75rem", color: "var(--slate-500)", marginTop: 2 }}>
                                    Category: <strong>{item.category_name}</strong>
                                  </div>
                                  <div style={{ fontSize: "0.75rem", marginTop: 4, fontWeight: 700, color: isOut ? "#DC2626" : "#B45309" }}>
                                    Current Stock: <strong>{item.current_stock}</strong> / Bottom Line: <strong>{item.min_stock_alert}</strong>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* 2. WARRANTY EXPIRY ALERTS */}
                      {(activeAlertTab === "all" || activeAlertTab === "warranty") && (
                        <>
                          {warrantyItems.map((item, idx) => {
                            const isExpired = item.status === "Expired" || (item.days_left !== undefined && item.days_left < 0);
                            return (
                              <div
                                key={`warranty-alert-${idx}`}
                                onClick={() => {
                                  setAlertsOpen(false);
                                  navigate(`/warranty?search=${encodeURIComponent(item.item_name || item.sku)}`);
                                }}
                                style={{
                                  padding: "12px 16px",
                                  borderBottom: "1px solid var(--slate-100)",
                                  display: "flex",
                                  gap: 12,
                                  cursor: "pointer",
                                  transition: "background 0.15s ease",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                              >
                                <div
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    backgroundColor: isExpired ? "#FEE2E2" : "#FFEDD5",
                                    color: isExpired ? "#DC2626" : "#EA580C",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    marginTop: 2,
                                  }}
                                >
                                  <FiClock size={16} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                                    <span style={{ fontWeight: 800, fontSize: "0.85rem", color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {item.item_name}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "0.68rem",
                                        fontWeight: 800,
                                        padding: "1px 6px",
                                        borderRadius: 4,
                                        backgroundColor: isExpired ? "#FEE2E2" : "#FFEDD5",
                                        color: isExpired ? "#DC2626" : "#C2410C",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {isExpired ? "Expired" : "Expiring Soon"}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: "0.75rem", color: "var(--slate-500)", marginTop: 2 }}>
                                    Party: {item.party_name}
                                  </div>
                                  <div style={{ fontSize: "0.74rem", marginTop: 4, color: isExpired ? "#DC2626" : "#EA580C", fontWeight: 600 }}>
                                    {isExpired
                                      ? "Warranty expired"
                                      : `Expires in ${item.days_left} ${item.days_left === 1 ? "day" : "days"}`} ({item.warranty_end_date ? new Date(item.warranty_end_date).toLocaleDateString() : ""})
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Footer Quick Links */}
                <div
                  style={{
                    padding: "10px 16px",
                    borderTop: "1px solid var(--slate-200)",
                    backgroundColor: "var(--slate-50)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "0.78rem",
                  }}
                >
                  <Link
                    to="/stock"
                    onClick={() => setAlertsOpen(false)}
                    style={{
                      color: "#B45309",
                      fontWeight: 700,
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    Stock Catalog <FiArrowRight size={13} />
                  </Link>

                  <Link
                    to="/warranty"
                    onClick={() => setAlertsOpen(false)}
                    style={{
                      color: "var(--primary)",
                      fontWeight: 700,
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    Warranty Tracker <FiArrowRight size={13} />
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* User Role Badge */}
          <div
            className="navbar-role-badge"
            style={{
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              display: "flex",
              alignItems: "center",
              gap: 6,
              backgroundColor: isSuperAdmin ? "rgba(16, 185, 129, 0.15)" : isAdmin ? "var(--primary-light)" : "var(--slate-100)",
              color: isSuperAdmin ? "var(--primary)" : isAdmin ? "var(--primary)" : "var(--slate-700)",
              border: `1px solid ${isSuperAdmin ? "rgba(16, 185, 129, 0.4)" : isAdmin ? "rgba(16, 185, 129, 0.3)" : "var(--slate-200)"}`,
            }}
          >
            <FiShield size={13} color={isSuperAdmin ? "var(--accent)" : isAdmin ? "var(--accent)" : "var(--slate-500)"} />
            {isSuperAdmin ? "Super Admin" : user?.type || user?.role || "User"}
          </div>

          {/* Profile Dropdown */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "none",
                border: "1px solid var(--slate-200)",
                padding: "6px 12px",
                borderRadius: "var(--radius-md)",
                cursor: "pointer",
                backgroundColor: menuOpen ? "var(--slate-50)" : "#fff",
                transition: "all 0.2s",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "var(--primary-light)",
                  color: "var(--primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                }}
              >
                {user?.name ? user.name.charAt(0).toUpperCase() : <FiUser size={16} />}
              </div>
              <div className="navbar-user-text" style={{ textAlign: "left" }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--navy)", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user?.name || user?.username || "Account"}
                </div>
                <div style={{ fontSize: "0.7rem", color: "var(--slate-500)" }}>
                  {user?.type || "Staff"}
                </div>
              </div>
            </button>

            {menuOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  marginTop: 8,
                  width: 230,
                  backgroundColor: "#ffffff",
                  borderRadius: "var(--radius-md)",
                  boxShadow: "var(--shadow-xl)",
                  border: "1px solid var(--slate-200)",
                  zIndex: 200,
                  padding: "8px 0",
                  animation: "fadeIn 0.2s ease-out",
                }}
              >
                <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--slate-100)" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--navy)" }}>{user?.name}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--slate-500)", marginTop: 2 }}>{user?.email}</div>
                </div>

                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setPwdModalOpen(true);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 16px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    color: "var(--slate-700)",
                    textAlign: "left",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <FiLock size={16} color="var(--slate-500)" />
                  Change Password
                </button>

                <div style={{ height: 1, backgroundColor: "var(--slate-100)", margin: "4px 0" }} />

                <button
                  onClick={handleLogout}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 16px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    color: "var(--danger)",
                    textAlign: "left",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--danger-bg)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <FiLogOut size={16} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Change Password Modal */}
      <Modal isOpen={pwdModalOpen} onClose={() => setPwdModalOpen(false)} maxWidth={420}>
        <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--navy)", marginBottom: 16 }}>
          Update Security Password
        </h3>
        <form onSubmit={handlePasswordChange}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "var(--slate-700)", marginBottom: 6 }}>
              Current Password
            </label>
            <input
              type="password"
              required
              value={passwords.currentPassword}
              onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                fontSize: "0.9rem",
              }}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "var(--slate-700)", marginBottom: 6 }}>
              New Password (min 6 chars)
            </label>
            <input
              type="password"
              required
              value={passwords.newPassword}
              onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                fontSize: "0.9rem",
              }}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "var(--slate-700)", marginBottom: 6 }}>
              Confirm New Password
            </label>
            <input
              type="password"
              required
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                fontSize: "0.9rem",
              }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={() => setPwdModalOpen(false)}
              style={{
                padding: "8px 16px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                background: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submittingPwd}
              style={{
                padding: "8px 18px",
                borderRadius: "var(--radius-md)",
                border: "none",
                background: "var(--primary)",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {submittingPwd ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default Navbar;
